<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import type { IReservationGuideContext } from '@/composition/reservations/reservation-guide-context';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    GROUP_PARTY_MAX,
    groupProblem,
    groupSeatsTotal,
  } from '@/domain/reservations/reservation-group-range';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IReservationTableRef } from '@/interfaces/IReservation';
  import type { IReservationTableGroup } from '@/interfaces/IReservationSettings';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  /**
   * One saved combination: «Mesa 3 + Mesa 4 · Entre 5 y 8 personas» with an
   * inline range editor behind the pencil. Self-contained: it writes through
   * the API itself and tells its owner what changed.
   */
  const props = defineProps<{
    group: IReservationTableGroup;
    tables: readonly IReservationTableRef[];
    /** Ajustes layout: a ⋯ menu with «Editar mesas» and «Eliminar» instead of «Quitar». */
    withMenu?: boolean;
    disabled?: boolean;
    /**
     * The guide's `run()`: the request then counts as in flight for its bar
     * and leave guards, and failures are toasted by it. Absent in the card.
     */
    run?: IReservationGuideContext['run'];
  }>();
  const emit = defineEmits<{
    (e: 'saved', group: IReservationTableGroup): void;
    (e: 'removed', group: IReservationTableGroup): void;
    (e: 'edit', group: IReservationTableGroup): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const editing = ref(false);
  const minParty = ref(props.group.minParty);
  const maxParty = ref(props.group.maxParty);
  const saving = ref(false);
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  /** The row still belongs to the branch on screen. */
  function current(): boolean {
    return (
      alive &&
      store.restaurant?.id === props.group.restaurantId &&
      store.selectedBranch?.id === props.group.branchId
    );
  }

  const members = computed(() =>
    props.group.tableIds
      .map(
        (id) =>
          props.tables.find((table) => table.id === id)?.name ?? String(id)
      )
      .join(' + ')
  );
  /** D6 names the combination after its tables; a custom name earns a second line. */
  const showMembers = computed(
    () =>
      members.value.trim().toLocaleLowerCase() !==
      props.group.name.trim().toLocaleLowerCase()
  );
  const rangeText = computed(() =>
    props.group.minParty === props.group.maxParty
      ? String(
          proxy.$tc('reservations.groups.row.exact', props.group.maxParty, {
            count: props.group.maxParty,
          })
        )
      : tr('reservations.groups.row.range', {
          min: props.group.minParty,
          max: props.group.maxParty,
        })
  );
  const problem = computed(() => groupProblem(props.group, props.tables));
  const problemText = computed(() => {
    switch (problem.value.kind) {
      case 'offline':
        return tr('reservations.groups.row.problemOffline');
      case 'belowMinimum':
        return tr('reservations.groups.row.problemBelowMinimum', {
          seats: problem.value.seats,
        });
      case 'overSeats':
        return tr('reservations.groups.row.problemOverSeats', {
          seats: problem.value.seats,
        });
      default:
        return '';
    }
  });
  const seats = computed(() =>
    groupSeatsTotal(props.group.tableIds, props.tables)
  );
  // «Hasta» never exceeds what the members seat: the API rejects more.
  const maxLimit = computed(() =>
    Math.max(minParty.value, Math.min(seats.value, GROUP_PARTY_MAX))
  );
  const busy = computed(() => saving.value || !!props.disabled);
  const editLabel = computed(() =>
    tr('reservations.groups.row.edit', { name: props.group.name })
  );

  function toggleEditing() {
    if (busy.value) return;
    if (!editing.value) {
      // Open on a range the API accepts, even if the stored one no longer fits.
      const cap = Math.max(1, Math.min(seats.value, GROUP_PARTY_MAX));
      maxParty.value = Math.min(props.group.maxParty, cap);
      minParty.value = Math.min(props.group.minParty, maxParty.value);
    }
    editing.value = !editing.value;
  }
  function setMin(value: number) {
    minParty.value = value;
    if (maxParty.value < value) maxParty.value = value;
  }
  function setMax(value: number) {
    maxParty.value = value;
    if (minParty.value > value) minParty.value = value;
  }
  /** Through the guide's `run()` when given (it toasts failures); the card's own try/catch otherwise. */
  async function request<T>(task: () => Promise<T>): Promise<T | null> {
    if (props.run) return props.run(task, tr('reservations.groups.error'));
    try {
      return await task();
    } catch (cause) {
      if (current()) ui.serverError(cause, tr('reservations.groups.error'));
      return null;
    }
  }
  async function saveRange() {
    if (busy.value || !current()) return;
    const group = props.group;
    saving.value = true;
    const saved = await request(() =>
      ReservationsService.updateTableGroup(group.id, {
        restaurantId: group.restaurantId,
        branchId: group.branchId,
        name: group.name,
        tableIds: group.tableIds,
        minParty: minParty.value,
        maxParty: maxParty.value,
      })
    );
    if (!current()) return;
    saving.value = false;
    if (!saved) return;
    editing.value = false;
    ui.showToast(AlertType.Success, tr('reservations.groups.saved'));
    emit('saved', saved);
  }
  async function remove() {
    if (busy.value) return;
    const group = props.group;
    const accepted = await modals.confirm({
      title: tr('reservations.groups.deleteTitle'),
      message: group.name,
      acceptText: tr('delete'),
      acceptColor: 'error',
    });
    if (!accepted || !current() || busy.value) return;
    saving.value = true;
    const removed = await request(async () => {
      await ReservationsService.deleteTableGroup(group.id);
      return true;
    });
    if (!current()) return;
    saving.value = false;
    if (!removed) return;
    ui.showToast(AlertType.Success, tr('reservations.groups.deleted'));
    emit('removed', group);
  }
</script>

<template>
  <article
    class="reservation-table-group-row"
    :class="{ 'with-menu': withMenu }"
  >
    <div class="row-main">
      <div class="row-details">
        <h3 class="group-name">{{ group.name }}</h3>
        <p v-if="showMembers" class="group-members">{{ members }}</p>
        <p class="group-range">{{ rangeText }}</p>
        <p v-if="problemText" class="group-problem">{{ problemText }}</p>
      </div>
      <div class="row-actions">
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="pencil-outline"
          :aria-label="editLabel"
          :aria-expanded="editing ? 'true' : 'false'"
          :disabled="busy"
          @click="toggleEditing"
        />
        <PosUiPopover
          v-if="withMenu"
          placement="bottom"
          align="end"
          :disabled="busy"
          ><template #trigger="{ toggle }"
            ><PosUiButton
              color="neutral"
              variant="ghost"
              icon="dots-horizontal"
              :aria-label="$t('posV2.moreActions')"
              :disabled="busy"
              @click="toggle" /></template
          ><template #default="{ close }"
            ><div class="actions-menu">
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="table-multiple"
                block
                @click="
                  close();
                  emit('edit', group);
                "
                >{{ $t('reservations.groups.row.editTables') }}</PosUiButton
              ><PosUiButton
                color="error"
                variant="ghost"
                icon="delete-outline"
                block
                @click="
                  close();
                  remove();
                "
                >{{ $t('delete') }}</PosUiButton
              >
            </div></template
          ></PosUiPopover
        >
        <PosUiButton
          v-else
          class="remove-button"
          color="neutral"
          variant="ghost"
          :aria-label="`${$t('reservations.guide.remove')} · ${group.name}`"
          :disabled="busy"
          @click="remove"
          >{{ $t('reservations.guide.remove') }}</PosUiButton
        >
      </div>
    </div>
    <div v-if="editing" class="row-editor" role="group" :aria-label="editLabel">
      <!-- Plain captions: a <label> would forward taps to the stepper's «−». -->
      <div class="range-field">
        <span class="range-caption">{{
          $t('reservations.groups.row.from')
        }}</span>
        <PosUiStepper
          :value="minParty"
          :min="1"
          :max="maxParty"
          size="md"
          editable
          :disabled="busy"
          :aria-label="$t('reservations.groups.row.from')"
          @input="setMin"
        />
      </div>
      <div class="range-field">
        <span class="range-caption">{{
          $t('reservations.groups.row.to')
        }}</span>
        <PosUiStepper
          :value="maxParty"
          :min="minParty"
          :max="maxLimit"
          size="md"
          editable
          :disabled="busy"
          :aria-label="$t('reservations.groups.row.to')"
          @input="setMax"
        />
      </div>
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="busy"
        @click="saveRange"
        >{{ $t('reservations.guide.done') }}</PosUiButton
      >
    </div>
  </article>
</template>

<style scoped lang="scss">
  .reservation-table-group-row {
    @apply tw-flex tw-flex-col tw-gap-3;
    .row-main {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3;
    }
    .row-details {
      @apply tw-min-w-0 tw-flex-1;
      p {
        @apply tw-m-0;
      }
    }
    .group-name {
      @apply tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .group-members {
      @apply tw-mt-0.5 tw-break-words tw-text-xs tw-text-neutral-500;
    }
    .group-range {
      @apply tw-mt-1 tw-text-sm tw-tabular-nums tw-text-neutral-700;
    }
    .group-problem {
      @apply tw-mt-1 tw-text-sm tw-text-amber-700;
    }
    .row-actions {
      @apply tw-flex tw-shrink-0 tw-items-center tw-gap-1;
      // Icon-only buttons stay square 44px targets.
      :deep(.pos-ui-button:not(.remove-button)) {
        @apply tw-w-11 tw-px-0;
      }
    }
    // Muted «Quitar»: restated with the button's own classes to beat its variant rule.
    .remove-button.pos-ui-button.color-neutral.variant-ghost {
      @apply tw-text-neutral-500;
    }
    .actions-menu {
      @apply tw-grid tw-gap-1;
    }
    .row-editor {
      @apply tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-rounded-lg tw-bg-canvas tw-p-3;
    }
    .range-field {
      @apply tw-flex tw-flex-col tw-gap-1;
      .range-caption {
        @apply tw-text-xs tw-font-medium tw-text-neutral-500;
      }
    }
  }
</style>
